import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Shield, AlertTriangle, Activity } from 'lucide-react';
import { CHART_SERIES } from '../../config/statusColors';
import {
  calcModifiedDuration,
  daysBetween,
  formatCurrency,
  calcPortfolioStats,
} from '../../lib/fixedIncome';
import { RISK_LIMITS, STRESS_SCENARIOS } from '../../data/portfolioMockData';
import ExportToolbar from './ExportToolbar';
import {
  exportMultiSheetExcel, exportToPdf,
  RISK_LIMITS_EXPORT_COLUMNS, STRESS_EXPORT_COLUMNS,
} from '../../lib/exportUtils';

// ---- Helpers ----------------------------------------------------------------

function calcMarketValueForPosition(p) {
  const { faceValue, couponRate = 0, yieldToMaturity = 0, maturityDate } = p;
  const daysRemaining = daysBetween(new Date(), new Date(maturityDate));
  const years = daysRemaining / 365;
  if (years <= 0) return faceValue;
  if (yieldToMaturity <= 0) return faceValue * (1 + couponRate * years);
  const periods = Math.max(1, Math.ceil(years));
  const couponPayment = faceValue * couponRate;
  let pv = 0;
  for (let t = 1; t <= periods; t++) {
    const discountFactor = Math.pow(1 + yieldToMaturity, -(t * years / periods));
    if (t < periods) {
      pv += couponPayment * (years / periods) * discountFactor;
    } else {
      pv += (faceValue + couponPayment * (years / periods)) * discountFactor;
    }
  }
  return pv;
}

function buildDurationByCountry(positions) {
  const map = {};
  (positions || []).forEach((p) => {
    const daysRemaining = daysBetween(new Date(), new Date(p.maturityDate));
    const maturityYears = daysRemaining / 365;
    const mv = calcMarketValueForPosition(p);
    const dur = calcModifiedDuration(p.couponRate || 0, p.yieldToMaturity || 0, maturityYears);

    if (!map[p.country]) {
      map[p.country] = { country: p.country, weightedDurSum: 0, marketValue: 0 };
    }
    map[p.country].weightedDurSum += dur * mv;
    map[p.country].marketValue += mv;
  });

  return Object.values(map)
    .map((g) => ({
      country: g.country,
      duration: g.marketValue > 0 ? parseFloat((g.weightedDurSum / g.marketValue).toFixed(3)) : 0,
      marketValue: g.marketValue,
    }))
    .sort((a, b) => b.duration - a.duration);
}

function calcTotalDv01(positions) {
  let total = 0;
  (positions || []).forEach((p) => {
    const daysRemaining = daysBetween(new Date(), new Date(p.maturityDate));
    const maturityYears = daysRemaining / 365;
    const mv = calcMarketValueForPosition(p);
    const dur = calcModifiedDuration(p.couponRate || 0, p.yieldToMaturity || 0, maturityYears);
    total += mv * dur * 0.0001;
  });
  return total;
}

// ---- Status badge styles ----------------------------------------------------

const STATUS_STYLES = {
  OK:      { backgroundColor: '#CDF3FC', color: '#1A7A8A' },
  Warning: { backgroundColor: '#FFF9D5', color: '#7A6B1A' },
  Breach:  { backgroundColor: '#FFC1DA', color: '#8B2252' },
};

function getLimitStatus(entry) {
  if (entry.limit === null) return { label: 'N/A', style: STATUS_STYLES.OK };
  const isMin = entry.name.toLowerCase().includes('(min)');
  if (isMin) {
    if (entry.current < entry.limit) return { label: 'Breach', style: STATUS_STYLES.Breach };
    if (entry.current < entry.limit * 1.15) return { label: 'Warning', style: STATUS_STYLES.Warning };
    return { label: 'OK', style: STATUS_STYLES.OK };
  }
  const ratio = entry.current / entry.limit;
  if (ratio > 0.9) return { label: 'Breach', style: STATUS_STYLES.Breach };
  if (ratio > 0.75) return { label: 'Warning', style: STATUS_STYLES.Warning };
  return { label: 'OK', style: STATUS_STYLES.OK };
}

function formatLimitValue(value, unit) {
  if (value === null) return 'N/A';
  if (unit === '%') return (value * 100).toFixed(0) + '%';
  if (unit === 'years') return value.toFixed(1) + ' yrs';
  if (unit === 'rating') return String(value);
  return String(value);
}

function formatHeadroom(entry) {
  if (entry.limit === null || typeof entry.current !== 'number') return 'N/A';
  const diff = entry.limit - entry.current;
  if (entry.unit === '%') return (diff * 100).toFixed(1) + '%';
  if (entry.unit === 'years') return diff.toFixed(2) + ' yrs';
  return diff.toFixed(4);
}

// ---- Section container ------------------------------------------------------

const Section = ({ title, children }) => (
  <div className="bg-white rounded-none border border-zinc-200 p-5 mt-6">
    <h3 className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 mb-4">{title}</h3>
    {children}
  </div>
);

// ---- Component --------------------------------------------------------------

const RiskTab = ({ positions, riskLimits: riskLimitsProp, stressScenarios: stressScenariosProp }) => {
  const riskLimitsData = riskLimitsProp || RISK_LIMITS;
  const stressData = stressScenariosProp || STRESS_SCENARIOS;

  const durationByCountry = buildDurationByCountry(positions);
  const totalDv01 = calcTotalDv01(positions);
  const stats = calcPortfolioStats(positions);

  const limitsBreached = riskLimitsData.filter((r) => {
    if (r.limit === null || typeof r.current !== 'number') return false;
    const isMin = r.name.toLowerCase().includes('(min)');
    return isMin ? r.current < r.limit : r.current > r.limit;
  }).length;

  const cards = [
    {
      title: 'Portfolio Duration',
      value: stats.weightedAvgDuration.toFixed(2) + ' years',
      Icon: Shield,
    },
    {
      title: 'Portfolio DV01',
      value: formatCurrency(totalDv01),
      Icon: Activity,
    },
    {
      title: 'Limits Breached',
      value: limitsBreached,
      Icon: AlertTriangle,
      highlight: limitsBreached > 0,
    },
  ];

  const handleExportExcel = () => {
    exportMultiSheetExcel([
      { data: riskLimitsData, columns: RISK_LIMITS_EXPORT_COLUMNS, sheetName: 'Risk Limits' },
      { data: stressData, columns: STRESS_EXPORT_COLUMNS, sheetName: 'Stress Scenarios' },
    ], 'portfolio-risk');
  };

  return (
    <div>
      {/* Export Toolbar */}
      <div className="flex justify-end mb-4">
        <ExportToolbar onExportExcel={handleExportExcel} onExportPdf={exportToPdf} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((card) => (
          <div
            key={card.title}
            className="bg-white rounded-none border border-zinc-200 p-5 hover:border-[#4D4D4D] transition-all"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">
                  {card.title}
                </p>
                <p
                  className={
                    'text-2xl font-bold font-sans mt-1 ' +
                    (card.highlight ? 'text-red-600' : 'text-black')
                  }
                >
                  {card.value}
                </p>
              </div>
              <div className="p-3 border border-zinc-300 bg-zinc-50 rounded-none">
                <card.Icon className="w-5 h-5 text-black" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Duration by Country Chart */}
      <Section title="Modified Duration by Market">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={durationByCountry} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
            <XAxis
              dataKey="country"
              tick={{ fontSize: 11, fontFamily: 'monospace' }}
              stroke="#a1a1aa"
            />
            <YAxis
              tickFormatter={(v) => v + ' yrs'}
              tick={{ fontSize: 11, fontFamily: 'monospace' }}
              stroke="#a1a1aa"
            />
            <Tooltip
              formatter={(value) => [Number(value).toFixed(3) + ' years', 'Duration']}
              contentStyle={{
                border: '1px solid #e4e4e7',
                borderRadius: 0,
                fontFamily: 'monospace',
                fontSize: 12,
              }}
            />
            <Bar dataKey="duration" fill={CHART_SERIES[1]} />
          </BarChart>
        </ResponsiveContainer>
      </Section>

      {/* Limit Monitoring Table */}
      <Section title="Risk Limit Monitoring">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                <th className="text-left px-4 py-2">Limit Name</th>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-right px-4 py-2">Limit</th>
                <th className="text-right px-4 py-2">Current</th>
                <th className="text-right px-4 py-2">Utilization</th>
                <th className="text-center px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Headroom</th>
              </tr>
            </thead>
            <tbody>
              {riskLimitsData.map((entry) => {
                const status = getLimitStatus(entry);
                const utilization =
                  entry.limit !== null && typeof entry.current === 'number'
                    ? ((entry.current / entry.limit) * 100).toFixed(0) + '%'
                    : 'N/A';

                return (
                  <tr
                    key={entry.name}
                    className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-black">{entry.name}</td>
                    <td className="px-4 py-3 text-zinc-500">{entry.category}</td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-600">
                      {formatLimitValue(entry.limit, entry.unit)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-black">
                      {formatLimitValue(entry.current, entry.unit)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-600">{utilization}</td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className="inline-block px-3 py-1 text-xs font-semibold rounded-none"
                        style={status.style}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-600">
                      {formatHeadroom(entry)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Stress Test Scenarios */}
      <Section title="Stress Test Scenarios">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                <th className="text-left px-4 py-2">Scenario</th>
                <th className="text-left px-4 py-2">Description</th>
                <th className="text-right px-4 py-2">Portfolio Value</th>
                <th className="text-right px-4 py-2">Yield Change</th>
                <th className="text-right px-4 py-2">Foreign Exchange Impact</th>
                <th className="text-right px-4 py-2">Net Impact</th>
              </tr>
            </thead>
            <tbody>
              {stressData.map((scenario) => {
                const fmt = (v) => (v * 100).toFixed(1) + '%';
                const colorFor = (v) => {
                  if (v < 0) return 'text-red-600';
                  if (v > 0) return 'text-green-600';
                  return 'text-zinc-600';
                };

                let netSeverityBg;
                if (scenario.netImpact <= -0.10) {
                  netSeverityBg = { backgroundColor: '#FFC1DA', color: '#8B2252' };
                } else if (scenario.netImpact <= -0.05) {
                  netSeverityBg = { backgroundColor: '#FFF9D5', color: '#7A6B1A' };
                } else if (scenario.netImpact < 0) {
                  netSeverityBg = { backgroundColor: '#FFE6D5', color: '#8B5A2B' };
                } else {
                  netSeverityBg = { backgroundColor: '#CDF3FC', color: '#1A7A8A' };
                }

                return (
                  <tr
                    key={scenario.name}
                    className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium text-black">{scenario.name}</td>
                    <td className="px-4 py-3 text-zinc-500 max-w-xs">{scenario.description}</td>
                    <td
                      className={
                        'px-4 py-3 text-right font-mono ' +
                        colorFor(scenario.portfolioValueImpact)
                      }
                    >
                      {fmt(scenario.portfolioValueImpact)}
                    </td>
                    <td
                      className={
                        'px-4 py-3 text-right font-mono ' +
                        colorFor(scenario.yieldChangeImpact)
                      }
                    >
                      {fmt(scenario.yieldChangeImpact)}
                    </td>
                    <td
                      className={
                        'px-4 py-3 text-right font-mono ' + colorFor(scenario.fxImpact)
                      }
                    >
                      {fmt(scenario.fxImpact)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span
                        className="inline-block px-3 py-1 text-xs font-bold font-mono rounded-none"
                        style={netSeverityBg}
                      >
                        {fmt(scenario.netImpact)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
};

export default RiskTab;
